/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { Fragment } from 'react'

import {
  EuiFlexGrid,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiSpacer,
  EuiTitle,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { EndpointsCloudPanel } from '@modules/resources-shared-components/EndpointsPanel/EndpointsCloudPanel'

import { getInstancesWithOverride } from '@/lib/stackDeployments/clusterInstances'
import {
  isAnyAutoscalingEnabledOnGet,
  isAutoscalingAvailable,
} from '@/lib/stackDeployments/selectors/autoscaling'
import { getDeploymentTopologyInstances } from '@/lib/stackDeployments/selectors/instances'
import { getCloudId } from '@/lib/stackDeployments/selectors/fundamentals'
import { isSliderInstanceTypeSupportedInTemplate } from '@/lib/sliders/support'

import DeploymentAlias from '../DeploymentAlias'
import DeploymentTagsPanel from '../DeploymentTagsPanel'
import DeploymentVersion from '../DeploymentVersion'
import CcsEditRemoteDeployments from '../CcsEditRemoteDeployments'
import { AppSearchDeploymentPageNotice } from '../../DeprecationNotices/AppsearchNotices'
import StackDeploymentName from '../../StackDeployments/StackDeploymentName'
import StackDeploymentNodesVisualization from '../../StackDeployments/StackDeploymentNodesVisualization'
import ResourceComments from '../../ResourceComments'
import AutoscalingStatusCallout from '../../Autoscaling/AutoscalingStatusCallout'
import IlmDeploymentMigrationCallout from '../../IlmMigration/IlmDeploymentMigrationCallout'
import FleetAvailableCallout from '../../Fleet/FleetAvailableCallout'
import InstanceOverrideCallout from '../InstanceOverrideCallout'
import AppSearchToEnterpriseSearchMigrationFlyout from '../../AppSearchToEnterpriseSearchMigration/AppSearchToEnterpriseSearchMigrationFlyout'
import { isCrossClusterSearch } from '../../../lib/deployments/ccs'
import CCSMigrationNotification from '../CCSMigrationNotification'
import DeploymentTemplate from '../../StackDeployments/DeploymentTemplate'
import DedicatedMastersUnnecessary from '../DedicatedMastersUnnecessary'
import SSOPortOverrideNotification from '../../StackDeploymentSlider/OverviewEnabled/SSOPortOverrideNotification'
import { DeploymentEndpointsPanel } from '../DeploymentEndpointsPanel/DeploymentEndpointsPanel'

import CustomerEngineerCallout from './CustomerEngineerCallout'

import type { FunctionComponent } from 'react'
import type { Props } from './types'

// Styles
const overviewGridStyles = css({
  '> .euiFlexItem': {
    minHeight: '4.5rem',
  },
})

const DeploymentOverview: FunctionComponent<Props> = ({
  stackDeployment,
  stackDeploymentId,
  deployment,
  deploymentTemplate,
  showNativeMemoryPressure,
  version,
  appSearchToEnterpriseSearchProgress,
}) => {
  const { regionId, plan, deploymentTemplateId, isSystemOwned } = deployment

  if (!stackDeployment) {
    return (
      <EuiFlexGroup justifyContent='center' alignItems='center'>
        <EuiLoadingSpinner size='xl' />
      </EuiFlexGroup>
    )
  }

  // tally running ES, Kibana, and APM instances
  const instances = getDeploymentTopologyInstances({ deployment: stackDeployment })
  const totalInstances = instances.length

  const instancesWithOverride = deploymentTemplate
    ? getInstancesWithOverride({
        instanceSummaries: instances,
        deploymentTemplate,
      })
    : []

  const pendingEs = plan.isPending || plan.waitingForPending

  const ccs = isCrossClusterSearch({
    deploymentTemplate,
    deploymentTemplateId,
    systemOwned: isSystemOwned,
  })

  const cloudId = getCloudId({ deployment: stackDeployment })

  const supportsAppsearch = isSliderInstanceTypeSupportedInTemplate(`appsearch`, deploymentTemplate)

  const deploymentOverviewKey = `deploymentOverview-${regionId}-${deployment.id}`

  const displayAutoscalingStatus =
    isAutoscalingAvailable(version) && isAnyAutoscalingEnabledOnGet({ deployment: stackDeployment })

  return (
    <div key={deploymentOverviewKey}>
      {appSearchToEnterpriseSearchProgress?.showUI && (
        <AppSearchToEnterpriseSearchMigrationFlyout deployment={stackDeployment} />
      )}
      <EuiFlexGroup direction='column' gutterSize='l'>
        <DedicatedMastersUnnecessary
          stackDeployment={stackDeployment}
          deploymentTemplate={deploymentTemplate}
        />
        <SSOPortOverrideNotification isDismissable={true} deployment={stackDeployment} />
        <IlmDeploymentMigrationCallout stackDeployment={stackDeployment} />
        <CCSMigrationNotification stackDeployment={stackDeployment} />
        <InstanceOverrideCallout instancesWithOverride={instancesWithOverride} />

        {supportsAppsearch && <AppSearchDeploymentPageNotice deployment={stackDeployment} />}

        <CustomerEngineerCallout deployment={stackDeployment} />

        <FleetAvailableCallout
          deployment={stackDeployment}
          deploymentTemplate={deploymentTemplate}
        />

        {displayAutoscalingStatus && <AutoscalingStatusCallout stackDeployment={stackDeployment} />}

        <div>
          {pendingEs || (
            <EuiFlexGroup direction='column' data-test-id='deployment-overview-es-not-pending'>
              <EuiFlexGrid columns={4} css={overviewGridStyles}>
                <EuiFlexItem>
                  <StackDeploymentName deployment={stackDeployment} />
                </EuiFlexItem>

                <EuiFlexItem>
                  <DeploymentAlias deployment={stackDeployment} />
                </EuiFlexItem>

                <EuiFlexItem>
                  {!deployment.isStopped && version && (
                    <Fragment>
                      <EuiSpacer size='xs' />
                      <DeploymentVersion deployment={stackDeployment} />
                    </Fragment>
                  )}
                </EuiFlexItem>
                <EuiFlexItem>
                  <DeploymentTemplate deployment={stackDeployment} />
                </EuiFlexItem>
              </EuiFlexGrid>

              <DeploymentTagsPanel deploymentId={stackDeploymentId} />

              <EuiFlexGroup>
                <EuiFlexItem>
                  <DeploymentEndpointsPanel deployment={stackDeployment} />
                </EuiFlexItem>
                <EuiFlexItem>{cloudId && <EndpointsCloudPanel cloudId={cloudId} />}</EuiFlexItem>
              </EuiFlexGroup>

              <EuiFlexItem>
                {/* eventually, we'll have _deployment level_ comments here,
                 * and the ES ones will be relegated to just the ES cluster
                 */}
                <ResourceComments
                  spacerBefore={true}
                  resourceType='elasticsearch'
                  regionId={regionId}
                  resourceId={deployment.id}
                />
              </EuiFlexItem>
            </EuiFlexGroup>
          )}
        </div>
      </EuiFlexGroup>

      {ccs && (
        <Fragment>
          <EuiSpacer size='m' />
          <CcsEditRemoteDeployments regionId={regionId} deployment={stackDeployment} />
        </Fragment>
      )}

      <EuiSpacer size='xl' />

      {totalInstances === 0 || (
        <StackDeploymentNodesVisualization
          title={
            <EuiTitle size='s'>
              <h3 data-test-id='deploymentOverview-zonesAndNodes'>
                <FormattedMessage id='deployment-info.instances' defaultMessage='Instances' />
              </h3>
            </EuiTitle>
          }
          deployment={stackDeployment}
          showNativeMemoryPressure={showNativeMemoryPressure}
        />
      )}
    </div>
  )
}

export default DeploymentOverview
